import PropTypes from 'prop-types';
// @mui
import { Button, TableCell, Tooltip, Typography } from '@mui/material';
// components
import { LoadingButton } from '@mui/lab';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from '../../../components/iconify/Iconify';
import SearchableTable from '../../../components/table/SearchableTable';
import EmptyContent from '../../../components/empty-content';
import RetryButton from '../../../components/buttons/RetryButton';
import { NotificationsContext } from '../../../contexts/NotificationsContext';

// ----------------------------------------------------------------------

const DEFAULT_TABLE_LABELS = [
  { id: 'title', label: 'Título' },
  { id: 'description', label: 'Descrição' },
  { id: 'date', label: 'Data' },
  { id: 'read', label: 'Lida' },
];

NotificationsTable.propTypes = {
  subheader: PropTypes.string,
  tableLabels: PropTypes.array,
};

export default function NotificationsTable({ subheader, tableLabels, ...other }) {
  const { count, table, notifications, fetching, isError, refetch, readAll, reading } =
    useContext(NotificationsContext);
  const navigate = useNavigate();

  // Notificação antiga tem link nulo: sem getRowSx ela mostraria cursor de pointer
  // e não navegaria, porque o SearchableTable define o cursor pela existência da
  // prop, não por linha.
  const getRowSx = (row) => ({ cursor: row.link ? 'pointer' : 'default' });

  // onRowClick e getRowSx são declarados DEPOIS do {...other} no JSX de propósito:
  // navegar para a venda é comportamento próprio desta tabela, não um ponto de
  // customização do consumidor. Antes do spread, qualquer onRowClick recebido por
  // prop desligaria a navegação em silêncio.
  const handleRowClick = (row) => row.link && navigate(row.link);

  const handleClearSearch = () => {
    table.setSearch('');
    table.setPage(1);
  };
  let emptyStateContent = (
    <EmptyContent
      title="Você ainda não possui notificações"
      description="Novidades e atualizações importantes aparecerão aqui."
      img="/assets/illustrations/illustration_empty_mail.svg"
      imgAlt="Nenhuma notificação"
    />
  );

  if (table.search?.length > 0) {
    emptyStateContent = (
      <EmptyContent
        title="Nenhuma notificação encontrada"
        description="Nenhuma notificação corresponde à pesquisa informada."
        img="/assets/illustrations/illustration_empty_mail.svg"
        imgAlt="Nenhuma notificação encontrada"
        action={
          <Button variant="outlined" onClick={handleClearSearch}>
            Limpar pesquisa
          </Button>
        }
      />
    );
  }

  if (isError && !fetching) {
    return <RetryButton message="Não foi possível carregar as notificações." onClick={refetch} />;
  }

  return (
    <SearchableTable
      count={count}
      fetching={fetching}
      labels={tableLabels?.length ? tableLabels : DEFAULT_TABLE_LABELS}
      rows={notifications}
      table={table}
      pageable
      emptyStateContent={emptyStateContent}
      Actions={
        <LoadingButton
          variant="contained"
          loading={reading}
          disabled={fetching || notifications.length === 0}
          onClick={readAll}
        >
          Ler todas
        </LoadingButton>
      }
      renderRow={(row) => <NotificationsTableRow row={row} />}
      {...other}
      onRowClick={handleRowClick}
      getRowSx={getRowSx}
    />
  );
}

// ----------------------------------------------------------------------

NotificationsTableRow.propTypes = {
  row: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    createdAt: PropTypes.string,
    read: PropTypes.bool,
  }),
};

function NotificationsTableRow({ row }) {
  return (
    <>
      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {row.title}
        </Typography>
      </TableCell>

      <TableCell>
        <Tooltip title={row.description}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              maxWidth: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.description}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {Intl.DateTimeFormat('pt-BR').format(new Date(row.createdAt))}
        </Typography>
      </TableCell>

      <TableCell>
        <Iconify
          icon={row.read ? 'eva:checkmark-circle-2-fill' : 'eva:close-circle-fill'}
          sx={{ color: row.read ? 'success.main' : 'error.main' }}
        />
      </TableCell>
    </>
  );
}
